import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Portal} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {type RefObject, useCallback, useMemo} from 'react'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'

import {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import {useIssueCreateDataContext} from '../contexts/IssueCreateDataContext'
import {CreateIssue, type CreateIssueProps} from '../CreateIssue'
import {CreateIssueFooter} from '../CreateIssueFooter'
import {useSafeClose} from '../hooks/use-safe-close'
import {useSimilarIssues} from '../hooks/use-similar-issues'
import type {IssueToPreheat, PotentialDuplicateIssue} from '../PotentialDuplicates'
import {DisplayMode} from '../utils/display-mode'
import {instanceOfIssueFormData, type OnCreateProps} from '../utils/model'
import {deriveIssueCreateContext} from '../utils/option-config'
import type {CreateIssueDialog$key} from './__generated__/CreateIssueDialog.graphql'
import styles from './CreateIssueDialog.module.css'
import {CreateIssueDialogHeader} from './CreateIssueDialogHeader'

type CreateIssueDialogProps = Omit<CreateIssueProps, 'currentRepository' | 'onSafeClose'> & {
  currentRepository: CreateIssueDialog$key | null | 'unset'
  returnFocusRef?: RefObject<HTMLElement | null>
  hideCreateMore?: boolean
  /** Called when user clicks on a similar issue. If not provided, issues open in new tab. */
  onDuplicateIssueSidePanelClick?: (issue: PotentialDuplicateIssue) => void
  /** Called when similar issues list changes. Used to preheat IssueViewer queries. */
  onDuplicateIssuesToPreheat?: (issues: IssueToPreheat[]) => void
}

export const CreateIssueDialog = ({
  onCreateSuccess,
  onCancel,
  navigate,
  currentRepository,
  isNavigatingToNew,
  returnFocusRef,
  onDuplicateIssueSidePanelClick,
  onDuplicateIssuesToPreheat,
  ...props
}: CreateIssueDialogProps): React.ReactElement => {
  const {optionConfig, initialDefaultDisplayMode, setDisplayMode, displayMode, setCreateMore} =
    useIssueCreateConfigContext()
  const {usedStorageKeyPrefix, title, body, issueFormBody, repository, template} = useIssueCreateDataContext()
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment CreateIssueDialog on Repository
      @argumentDefinitions(includeTemplates: {type: "Boolean", defaultValue: false}) {
        ...CreateIssue @arguments(includeTemplates: $includeTemplates)
      }
    `,
    currentRepository !== 'unset' ? currentRepository : null,
  )

  // Duplicate detection hook (disabled for issue duplication flow)
  const isIssueForm = template && instanceOfIssueFormData(template.data)
  const similarIssuesBody = isIssueForm ? issueFormBody : body
  const duplicateDetection = useSimilarIssues({
    repoOwner: repository?.owner.login ?? '',
    repoName: repository?.name ?? '',
    title,
    body: similarIssuesBody,
    template,
    issueFormRef: props.issueFormRef,
    isSimilarIssuesEnabled:
      displayMode !== DisplayMode.IssueDuplication &&
      isFeatureEnabled('semantic_similarity_duplicate_issue_detection') &&
      !!repository,
    context: deriveIssueCreateContext(optionConfig),
  })

  const onCancelInternal = useCallback(() => {
    setCreateMore(false)
    setDisplayMode(initialDefaultDisplayMode)

    onCancel()
  }, [initialDefaultDisplayMode, onCancel, setCreateMore, setDisplayMode])

  const {onSafeClose} = useSafeClose({
    storageKeyPrefix: usedStorageKeyPrefix,
    issueFormRef: props.issueFormRef,
    onCancel: onCancelInternal,
  })

  const resetDisplayModeOnCreateSuccess = useCallback(
    ({issue, createMore}: OnCreateProps) => {
      onCreateSuccess({issue, createMore})
      if (!createMore) {
        setDisplayMode(initialDefaultDisplayMode)
      }
    },
    [initialDefaultDisplayMode, onCreateSuccess, setDisplayMode],
  )

  const issueDialogProps = {...props, onCancel: onSafeClose}

  const showCreateDialog = useMemo(() => {
    return !isNavigatingToNew || !optionConfig.canBypassTemplateSelection
  }, [isNavigatingToNew, optionConfig.canBypassTemplateSelection])

  const showDuplicateDetection =
    duplicateDetection &&
    duplicateDetection.isDuplicateDetectionEnabled &&
    duplicateDetection.items.length > 0 &&
    !duplicateDetection.isError

  return (
    <Portal>
      <Dialog
        returnFocusRef={returnFocusRef}
        renderHeader={headerProps => <CreateIssueDialogHeader navigate={navigate} {...headerProps} />}
        renderFooter={() => {
          if (displayMode === DisplayMode.IssueCreation || displayMode === DisplayMode.IssueDuplication) {
            return (
              <Dialog.Footer className={clsx(showDuplicateDetection && styles.footerWithDuplicatesShown)}>
                <CreateIssueFooter
                  onClose={() => onSafeClose()}
                  onDuplicateIssueSidePanelClick={onDuplicateIssueSidePanelClick}
                  onDuplicateIssuesToPreheat={onDuplicateIssuesToPreheat}
                  duplicateDetection={duplicateDetection}
                  {...props}
                />
              </Dialog.Footer>
            )
          }
          return null
        }}
        renderBody={() => (
          <div
            className={`${
              displayMode === DisplayMode.IssueCreation || displayMode === DisplayMode.IssueDuplication
                ? 'tmp-p-3'
                : 'p-0'
            }`}
          >
            <CreateIssue
              onCreateSuccess={resetDisplayModeOnCreateSuccess}
              navigate={navigate}
              currentRepository={currentRepository === 'unset' ? 'unset' : data || null}
              duplicateDetection={duplicateDetection}
              {...issueDialogProps}
            />
          </div>
        )}
        onClose={onSafeClose}
        width="xlarge"
        height="auto"
        className={clsx(styles.CreateIssueDialogContainer, !showCreateDialog && styles.dialogHidden)}
      />
    </Portal>
  )
}
